<template>
    <div class="min-h-screen relative overflow-hidden">
        <!-- Single Parent Background with Blur -->
        <div class="absolute inset-0 bg-cover bg-center blur-sm" style="background-image: url('/images/bg.avif');"></div>
        <div class="absolute inset-0 bg-gradient-to-br from-blue-600/70 via-blue-700/70 to-blue-800/70"></div>

        <!-- Content Container -->
        <div class="relative z-10 min-h-screen flex">
            <!-- Left Side - Hero Section -->
            <div class="hidden lg:flex lg:w-1/2 flex-col justify-between px-16 py-12 text-white">
                <!-- Top Section: Logo & Brand -->
                <div class="space-y-2">
                    <div class="flex items-center gap-3">
                        <div class="w-12 h-12 bg-white/10 backdrop-blur-md rounded-xl flex items-center justify-center border border-white/20">
                            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"></path>
                            </svg>
                        </div>
                        <div>
                            <h1 class="text-2xl font-semibold tracking-tight text-white">Sides Ford</h1>
                            <p class="text-white/60 text-xs font-medium uppercase tracking-wider">Inventory Management</p>
                        </div>
                    </div>
                </div>

                <!-- Middle Section: Hero Content -->
                <div class="space-y-8 my-auto">
                    <div class="space-y-6">
                        <h2 class="text-5xl font-bold leading-tight text-white tracking-tight">
                            Modern inventory management for the automotive industry
                        </h2>
                        <p class="text-lg text-white/75 leading-relaxed max-w-xl font-light">
                            Streamline operations, track vehicles in real-time, manage inventory efficiently, and generate comprehensive reports with powerful analytics using our advanced automotive management platform designed for modern dealerships.
                        </p>
                    </div>
                </div>

                <!-- Bottom Section: Rotating Carousel -->
                <div class="relative">
                    <!-- Main Feature Card -->
                    <div class="relative bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-6 min-h-[140px]">
                        <div class="carousel-wrapper">
                            <!-- Feature 1: Lightning Fast -->
                            <div class="carousel-item" :class="{ 'active': currentFeature === 0 }">
                                <div class="flex items-start gap-5">
                                    <div class="w-16 h-16 bg-gradient-to-br from-blue-400/30 to-blue-600/30 rounded-2xl flex items-center justify-center flex-shrink-0 border border-blue-400/20">
                                        <svg class="w-8 h-8 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                                        </svg>
                                    </div>
                                    <div class="flex-1">
                                        <h3 class="text-xl font-bold text-white mb-2">Lightning Fast</h3>
                                        <p class="text-white/70 text-sm leading-relaxed">Experience blazing-fast performance designed for high-volume operations and seamless workflow.</p>
                                    </div>
                                </div>
                            </div>

                            <!-- Feature 2: Real-time Analytics -->
                            <div class="carousel-item" :class="{ 'active': currentFeature === 1 }">
                                <div class="flex items-start gap-5">
                                    <div class="w-16 h-16 bg-gradient-to-br from-purple-400/30 to-purple-600/30 rounded-2xl flex items-center justify-center flex-shrink-0 border border-purple-400/20">
                                        <svg class="w-8 h-8 text-purple-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                                        </svg>
                                    </div>
                                    <div class="flex-1">
                                        <h3 class="text-xl font-bold text-white mb-2">Real-time Analytics</h3>
                                        <p class="text-white/70 text-sm leading-relaxed">Make data-driven decisions with live tracking and powerful insights at your fingertips.</p>
                                    </div>
                                </div>
                            </div>

                            <!-- Feature 3: Enterprise Security -->
                            <div class="carousel-item" :class="{ 'active': currentFeature === 2 }">
                                <div class="flex items-start gap-5">
                                    <div class="w-16 h-16 bg-gradient-to-br from-emerald-400/30 to-emerald-600/30 rounded-2xl flex items-center justify-center flex-shrink-0 border border-emerald-400/20">
                                        <svg class="w-8 h-8 text-emerald-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                                        </svg>
                                    </div>
                                    <div class="flex-1">
                                        <h3 class="text-xl font-bold text-white mb-2">Enterprise Security</h3>
                                        <p class="text-white/70 text-sm leading-relaxed">Your data is protected with bank-grade encryption and industry-leading security standards.</p>
                                    </div>
                                </div>
                            </div>

                            <!-- Feature 4: Smart Automation -->
                            <div class="carousel-item" :class="{ 'active': currentFeature === 3 }">
                                <div class="flex items-start gap-5">
                                    <div class="w-16 h-16 bg-gradient-to-br from-amber-400/30 to-amber-600/30 rounded-2xl flex items-center justify-center flex-shrink-0 border border-amber-400/20">
                                        <svg class="w-8 h-8 text-amber-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                        </svg>
                                    </div>
                                    <div class="flex-1">
                                        <h3 class="text-xl font-bold text-white mb-2">Smart Automation</h3>
                                        <p class="text-white/70 text-sm leading-relaxed">Optimize your workflow with intelligent automation that saves time and reduces errors.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Carousel Indicators -->
                    <div class="flex items-center justify-center gap-2 mt-4">
                        <button
                            v-for="index in 4"
                            :key="index"
                            @click="currentFeature = index - 1"
                            class="w-2 h-2 rounded-full transition-all duration-300"
                            :class="currentFeature === index - 1 ? 'bg-white w-8' : 'bg-white/40 hover:bg-white/60'"
                        ></button>
                    </div>
                </div>
            </div>

            <!-- Right Side - Login Form -->
            <div class="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-12">
                <div class="w-full max-w-md">
                    <!-- Modern Card with Better Glassmorphism -->
                    <div class="backdrop-blur-2xl bg-white/15 border border-white/20 rounded-3xl p-10 shadow-[0_8px_32px_0_rgba(0,0,0,0.2)]">
                        <!-- Welcome Header -->
                        <div class="text-center mb-8">
                            <h2 class="text-4xl font-bold text-white mb-2 tracking-tight">Welcome back</h2>
                            <p class="text-white/70 text-base">Please sign in to your account</p>
                        </div>

                        <!-- Login Form -->
                        <form id="loginForm" @submit.prevent="refreshCsrfToken" class="space-y-5">
                            <!-- Error Messages -->
                            <div v-if="errors" class="bg-red-50/95 backdrop-blur-sm border border-red-200 rounded-2xl p-4 shadow-sm">
                                <div class="flex items-start gap-3">
                                    <div class="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                                        <svg class="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
                                        </svg>
                                    </div>
                                    <div class="flex-1">
                                        <h3 class="text-red-900 font-bold text-sm mb-1">Authentication Failed</h3>
                                        <p class="text-red-700 text-sm leading-relaxed">
                                            <span v-if="errors.length === 1">{{ errors[0] }}</span>
                                            <span v-else>
                                                <ul class="list-disc list-inside space-y-0.5">
                                                    <li v-for="error in errors" :key="error">{{ error }}</li>
                                                </ul>
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <!-- Email Input -->
                            <div>
                                <label for="email" class="block text-sm font-semibold text-white mb-2.5">
                                    Email address
                                </label>
                                <div class="relative">
                                    <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                                        <svg class="h-5 w-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
                                            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
                                        </svg>
                                    </div>
                                    <input
                                        id="email"
                                        type="email"
                                        name="email"
                                        required
                                        autofocus
                                        v-model="form.email"
                                        class="w-full pl-12 pr-4 py-3.5 bg-white/90 backdrop-blur-sm border border-white/40 rounded-2xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/60 transition-all duration-200 font-medium"
                                        placeholder="Enter your email"
                                    />
                                </div>
                            </div>

                            <!-- Password Input -->
                            <div>
                                <label for="password" class="block text-sm font-semibold text-white mb-2.5">
                                    Password
                                </label>
                                <div class="relative">
                                    <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                                        <svg class="h-5 w-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"></path>
                                        </svg>
                                    </div>
                                    <input
                                        id="password"
                                        :type="showPassword ? 'text' : 'password'"
                                        name="password"
                                        required
                                        v-model="form.password"
                                        autocomplete="current-password"
                                        class="w-full pl-12 pr-14 py-3.5 bg-white/90 backdrop-blur-sm border border-white/40 rounded-2xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/60 transition-all duration-200 font-medium"
                                        placeholder="Enter your password"
                                    />
                                    <div class="absolute inset-y-0 right-0 flex items-center pr-4 z-10 pointer-events-none">
                                        <svg
                                            v-if="!showPassword"
                                            @click="showPassword = !showPassword"
                                            class="h-5 w-5 text-gray-500 cursor-pointer pointer-events-auto"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"></path>
                                            <path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd"></path>
                                        </svg>
                                        <svg
                                            v-else
                                            @click="showPassword = !showPassword"
                                            class="h-5 w-5 text-gray-500 cursor-pointer pointer-events-auto"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path fill-rule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clip-rule="evenodd"></path>
                                            <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z"></path>
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            <!-- Submit Button -->
                            <button
                                type="submit"
                                :disabled="loading"
                                class="w-full bg-white hover:bg-white/95 text-blue-600 font-bold py-4 px-4 rounded-2xl shadow-lg hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-transparent disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none mt-6"
                            >
                                <span v-if="loading" class="flex items-center justify-center">
                                    <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Signing in...
                                </span>
                                <span v-else>Sign in</span>
                            </button>
                        </form>

                        <!-- Divider -->
                        <div class="relative my-8">
                            <div class="absolute inset-0 flex items-center">
                                <div class="w-full border-t border-white/20"></div>
                            </div>
                        </div>

                        <!-- Footer -->
                        <div class="text-center space-y-3">
                            <p class="text-sm text-white/60">
                                <span v-if="hour < 12">Good morning</span>
                                <span v-else-if="hour < 18">Good afternoon</span>
                                <span v-else>Good evening</span>
                                • {{ new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) }}
                            </p>
                            <p class="text-xs text-white/50">
                                Developed by
                                <a href="https://carblue.com" target="_blank" class="text-white/70 hover:text-white font-medium transition-colors">
                                    carBlue
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import axios from 'axios';
import { getUserPermissionsLogin } from "./../queries/userPermission.graphql";

export default {
  data(){
      return{
        loading:0,
        hour:new Date().getHours(),
        currentFeature: 0,
        carouselInterval: null,
        showPassword: false,
        form:{
            email:null,
            password:null,
            acceptJson:'1',
        },
        errors:null,
      }
  },
  created(){
        localStorage.clear();
  },
  mounted() {
        // Auto-rotate carousel every 4 seconds
        this.carouselInterval = setInterval(() => {
            this.currentFeature = (this.currentFeature + 1) % 4;
        }, 4000);
  },
  beforeUnmount() {
        if (this.carouselInterval) {
            clearInterval(this.carouselInterval);
        }
  },
  methods: {
        refreshCsrfToken() {
            this.loading = 1;
            fetch('/refresh-csrf')
                .then(response => response.json())
                .then(data => {
                const csrfToken = document.querySelector('meta[name="csrf-token"]');
                csrfToken.setAttribute('content', data.token);
                const csrfInputs = document.querySelectorAll('input[name="_token"]');
                csrfInputs.forEach(input => input.value = data.token);

                // Clear previous errors
                this.errors = null;

                // Make login request
                axios.post('/api/login', this.form)
                    .then(res => {
                    if (res.data) {
                        localStorage.setItem('current_user', JSON.stringify(res.data))
                        this.$apollo
                        .query({
                            query: getUserPermissionsLogin,
                            variables: {
                            user_id: res.data?.id.toString()
                            }
                        })
                        .then(res => {
                            localStorage.setItem('user_permission', res.data.userPermissionsLogin);
                            let current_role = JSON.parse(localStorage.getItem('current_user')).role.toString();
                            if(current_role == '7'){
                                window.location.href = '/banker';
                            }
                            else if(current_role == '8'){
                                window.location.href = '/pictures';
                            }
                            else if(current_role == '3'){
                                window.location.href = '/checkin';
                            }
                            else if(current_role == '10'){
                                window.location.href = '/clerk';
                            }
                            else{
                                window.location.href = '/dashboard';
                            }
                        })
                        .catch(err => {
                            this.loading = 0;
                            console.error('GraphQL Error:', err);
                            this.errors = ['Error retrieving user permissions. Please try again.'];
                        });
                    }
                    })
                    .catch(err => {
                        this.loading = 0;
                        console.error('Login Error:', err);

                        if (err.response && err.response.data && err.response.data.errors) {
                            // Capture validation or server errors
                            this.errors = Object.values(err.response.data.errors).flat();
                        } else {
                            this.errors = ['An unexpected error occurred. Please try again.'];
                        }
                    });
                })
                .catch(error => {
                    this.loading = 0;
                    console.error('Error refreshing CSRF token:', error);
                    this.errors = ['Error refreshing CSRF token. Please try again.'];
                });
        }
  },
};
</script>
<style scoped>
        /* Input focus enhancements */
        input:focus {
            box-shadow: 0 0 0 3px rgba(3, 51, 149, 0.1);
        }

        /* Button hover enhancements */
        button:hover:not(:disabled) {
            box-shadow: 0 10px 25px rgba(3, 51, 149, 0.3);
        }

        /* Carousel Animation */
        .carousel-wrapper {
            position: relative;
        }

        .carousel-item {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            opacity: 0;
            visibility: hidden;
            transform: translateY(10px);
            transition: opacity 0.8s ease-in-out,
                        transform 0.8s ease-in-out,
                        visibility 0s linear 0.8s;
        }

        .carousel-item.active {
            position: relative;
            opacity: 1;
            visibility: visible;
            transform: translateY(0);
            transition: opacity 0.8s ease-in-out,
                        transform 0.8s ease-in-out,
                        visibility 0s linear 0s;
        }
</style>